// File: V2RayTun_Info.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Popconfirm, message } from "antd";
import { CopyOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import QRCode from "react-qr-code";
import { getCustomVlessLink, resetV2Devices } from "../api_v2/vpn";

const APP_LINKS = [
  {
    label: "Happ iOS",
    platform: "ios",
    icon: "/apple.svg",
    iconAlt: "Apple",
    variant: "ios",
    url: "https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973",
  },
  {
    label: "Happ Android",
    platform: "android",
    icon: "/Android.svg",
    iconAlt: "Android",
    variant: "android",
    url: "https://www.happ.su/main",
  },
  {
    label: "Happ Windows",
    platform: "windows",
    icon: "/win.svg",
    iconAlt: "Windows",
    variant: "windows",
    url: "https://simolapps.ru/downloads/apps/Happ-Windows-x64.exe",
  },
  {
    label: "Happ macOS",
    platform: "macos",
    icon: "/apple.svg",
    iconAlt: "Apple",
    variant: "ios",
    url: "https://simolapps.ru/downloads/apps/Happ-macOS-universal.dmg",
  },
];

export default function V2RayTun_Info({ user }) {
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState("");
  const [error, setError] = useState(null);
  const [deviceWarning, setDeviceWarning] = useState(null);
  const [queueStatus, setQueueStatus] = useState("");
  const requestControllerRef = useRef(null);
  const userAgent = navigator.userAgent || navigator.vendor || "";
  const platform = /android/i.test(userAgent)
    ? "android"
    : /iPad|iPhone|iPod/.test(userAgent)
      ? "ios"
      : /Windows/i.test(userAgent)
        ? "windows"
        : /Macintosh|Mac OS X/i.test(userAgent)
          ? "macos"
          : null;
  const visibleApps = platform
    ? APP_LINKS.filter((app) => app.platform === platform)
    : APP_LINKS;

  const resetState = () => {
    setError(null);
    setSubscriptionUrl("");
    setDeviceWarning(null);
    setQueueStatus("");
  };

  useEffect(() => () => requestControllerRef.current?.abort(), []);

  const loadProfiles = useCallback(async () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setLoading(true);
    resetState();

    try {
      const data = await getCustomVlessLink({
        signal: controller.signal,
        onStatus: (job, pollNumber) => {
          if (controller.signal.aborted) return;
          setQueueStatus(
            job.status === "processing"
              ? `Профиль создаётся. Проверка ${pollNumber + 1}.`
              : `Профиль поставлен в очередь. Проверка ${pollNumber + 1}.`
          );
        },
      });
      if (data.success !== true) {
        setError(data.error || data.message || "Не удалось получить ссылку");
        return;
      }

      setSubscriptionUrl(String(data.subscription_url || ""));
      if (data.device_warning_message) {
        setDeviceWarning({
          level: data.device_warning_level || "none",
          message: data.device_warning_message,
          count: data.device_count,
          limit: data.device_limit,
        });
      }
      if (!data.subscription_url) {
        if (data.job?.status === "error") {
          setError(data.message || "Не удалось создать VPN-профиль");
          setQueueStatus("");
        } else if (data.job) {
          setQueueStatus("Профиль ещё создаётся. Можно закрыть страницу и повторить проверку позже.");
        } else {
          setError("Ссылка не получена");
        }
      }
    } catch (e) {
      if (controller.signal.aborted || e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      setError(`Ошибка сети: ${String(e)}`);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Ссылка скопирована");
  };

  const resetDevices = async () => {
    setResetting(true);
    setError(null);
    try {
      const data = await resetV2Devices();
      if (!data.success || !data.subscription_url) {
        setError(data.message || "Не удалось сбросить устройства");
        return;
      }
      setSubscriptionUrl(String(data.subscription_url));
      setDeviceWarning({
        level: "none",
        message: "Все устройства Happ отвязаны. Добавьте новую ссылку в Happ — первое устройство станет единственным разрешённым.",
        count: 0,
        limit: 1,
      });
      setQueueStatus("");
      message.success(data.message || "Новая ссылка Happ готова");
    } catch (e) {
      setError(e?.response?.data?.message || "Не удалось сбросить устройства");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="v2ray-panel">
      <div className="v2ray-panel__header">
        <h2>Подключить VPN</h2>
      </div>

      <div className="v2ray-panel__action">
        <Button
          type="primary"
          size="large"
          icon={subscriptionUrl ? <CopyOutlined /> : <DownloadOutlined />}
          loading={loading}
          className="v2ray-panel__cta"
          onClick={() => {
            if (subscriptionUrl) {
              copyToClipboard(subscriptionUrl);
            } else {
              loadProfiles();
            }
          }}
        >
          {subscriptionUrl ? "Скопировать ссылку Happ" : "Получить ссылку Happ"}
        </Button>
        <Popconfirm
          title="Сбросить старое устройство?"
          description="Старая ссылка перестанет обновляться. Вы получите новую ссылку для Happ."
          okText="Сбросить"
          cancelText="Отмена"
          onConfirm={resetDevices}
        >
          <Button danger icon={<DeleteOutlined />} loading={resetting} disabled={loading}>
            Отвязать все устройства и получить новую ссылку
          </Button>
        </Popconfirm>
      </div>

      {error && (
        <Alert
          type="error"
          message="Ошибка"
          description={<pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{String(error)}</pre>}
        />
      )}

      {queueStatus && !error && (
        <Alert
          type="info"
          showIcon
          message="Создание VPN-профиля"
          description={queueStatus}
        />
      )}

      {deviceWarning && (
        <Alert
          type={deviceWarning.level === "blocked" ? "error" : deviceWarning.level === "none" ? "info" : "warning"}
          showIcon
          message={deviceWarning.level === "none" ? "Happ готов к подключению" : "Устройство Happ уже привязано"}
          description={
            <div>
              <div>{deviceWarning.message}</div>
              {typeof deviceWarning.count === "number" && typeof deviceWarning.limit === "number" && (
                <div style={{ marginTop: 6 }}>
                  Устройств: {deviceWarning.count}. Лимит: {deviceWarning.limit}.
                </div>
              )}
            </div>
          }
        />
      )}

      <div className="v2ray-panel__apps">
        <div className="v2ray-panel__apps-title">
          <span>Приложения</span>
        </div>
        <div className="v2ray-panel__apps-grid">
          {visibleApps.map((app) => (
            <button
              key={app.label}
              className="v2ray-panel__app-card"
              onClick={() => window.open(app.url, "_blank")}
            >
              <img
                className={`v2ray-panel__app-icon v2ray-panel__app-icon--${app.variant}`}
                src={app.icon}
                alt={app.iconAlt}
              />
              <span>{app.label}</span>
              <span className="v2ray-panel__app-arrow" aria-hidden="true">›</span>
            </button>
          ))}
        </div>
      </div>

      {subscriptionUrl && (
        <div className="v2ray-panel__qr">
          <div className="v2ray-panel__apps-title">
            <span>QR-код</span>
          </div>
          <div className="v2ray-panel__qr-box">
            <QRCode value={subscriptionUrl} size={168} />
          </div>
          <div className="v2ray-panel__qr-hint">Сканируйте для быстрого импорта</div>
        </div>
      )}

      <details className="v2ray-panel__help">
        <summary>Помощь с подключением</summary>
        <div>
          <div className="v2ray-panel__video-frame">
            <video controls preload="metadata" playsInline className="v2ray-panel__video">
              <source src="/HAPP.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="v2ray-panel__help-grid">
            <div>
              <strong>Для iOS:</strong>
              <span>
                Установите Happ, нажмите «Получить профиль» и добавьте скопированную ссылку в Happ. Профиль импортируется
                автоматически.
              </span>
            </div>
            <div>
              <strong>Для Android:</strong>
              <span>
                Установите Happ, скопируйте ссылку профиля, в приложении нажмите «+» и импортируйте её, затем
                нажмите «Connect».
              </span>
            </div>
            <div>
              <strong>Для Windows:</strong>
              <span>
                Установите Happ, получите ссылку профиля в кабинете и импортируйте её в приложение.
              </span>
            </div>
            <div>
              <strong>Для macOS:</strong>
              <span>
                Установите Happ, получите ссылку профиля в кабинете и добавьте её в приложение.
              </span>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
}
